/**
 * Attribution capture · Mujeres Motor.
 *
 * On every page view, if the URL carries any utm_* parameter, persist them
 * along with referrer + landing path to localStorage["mm_attribution"]
 * with a 30-day TTL. Forms read getAttribution() and ship it on submit so
 * the backend can attribute the lead.
 */

const KEY = "mm_attribution";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readStored() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.captured_at) return null;
    if (Date.now() - parsed.captured_at > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveStored(attr) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(attr));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Inspect current URL for UTM params; if present and we don't already have
 * a stored attribution (first-touch wins), persist the new one.
 *
 * Always update `current_path` on the existing attribution so the admin can
 * see the most recent page the user was on before submitting.
 */
export function captureFromLocation() {
  if (typeof window === "undefined") return null;

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return readStored();
  }

  const incoming = {};
  let hasUtm = false;
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) {
      incoming[k] = v.slice(0, 200);
      hasUtm = true;
    }
  }

  const existing = readStored();
  const path = window.location.pathname + window.location.search;

  if (hasUtm) {
    // First-touch wins for utm_*; if user already has UTMs we keep them and
    // only refresh current_path. If a *new* explicit campaign comes in we
    // overwrite (last-touch on UTM, conservative win on path).
    const next = {
      ...incoming,
      landing_path: existing?.landing_path || path,
      landing_timestamp: existing?.landing_timestamp || new Date().toISOString(),
      referrer: existing?.referrer || (document.referrer || ""),
      current_path: path,
      captured_at: Date.now(),
    };
    // If this is the first capture, also stamp the referrer fresh.
    if (!existing) {
      next.referrer = document.referrer || "";
    } else {
      // Overwrite UTMs with the new explicit campaign
      for (const k of UTM_KEYS) {
        if (incoming[k]) next[k] = incoming[k];
        else if (existing[k]) next[k] = existing[k];
      }
    }
    saveStored(next);
    return next;
  }

  if (existing) {
    // No new UTM, just refresh current_path so the form ships the latest one
    const next = { ...existing, current_path: path };
    saveStored(next);
    return next;
  }

  // No UTM ever and no stored attribution. Capture a "direct" landing
  // attribution once so we still know where they entered the site.
  const next = {
    landing_path: path,
    landing_timestamp: new Date().toISOString(),
    referrer: document.referrer || "",
    current_path: path,
    captured_at: Date.now(),
  };
  saveStored(next);
  return next;
}

export function getAttribution() {
  if (typeof window === "undefined") return null;
  const stored = readStored();
  if (!stored) return null;
  // Always refresh current_path on read so submit captures latest path
  const current = window.location.pathname + window.location.search;
  if (stored.current_path !== current) {
    stored.current_path = current;
    saveStored(stored);
  }
  return stored;
}

export function clearAttribution() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
